import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isBlank } from 'utils/helpers-ts';
import { TFieldValidatorFn, TValidationMode } from './types';
import { useValidationContext } from './ValidationContext';

const REQUIRED_ERROR = 'Obligatoriskt fält';

export type TUseFieldValidation = {
  mode?: TValidationMode;
  required?: boolean;
  value: any;
  validator?: TFieldValidatorFn;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

export type TUseFieldValidationReturn = {
  mode?: TValidationMode;
  error?: string;
  getError: (value?: any) => string | undefined;
  validate: (value?: any) => string | undefined;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

const useFieldValidation = (name: string, params?: TUseFieldValidation): TUseFieldValidationReturn => {
  const { mode: modeParam, required, value, validator, onBlur } = params || {};

  const { fieldsRef, globalMode, debounceMs } = useValidationContext();

  const keepRef = useRef({
    timeoutId: null as NodeJS.Timeout | null,
    validator,
    onBlur,
  });
  keepRef.current.validator = validator;
  keepRef.current.onBlur = onBlur;

  const [error, setError] = useState<string>();
  const [isBlured, setIsBlured] = useState(false);

  const mode = useMemo(() => {
    return modeParam || globalMode;
  }, [modeParam, globalMode]);

  const getError = useCallback(
    (valueParam?: any) => {
      const checkingValue = valueParam ?? value;
      if (required && isBlank(checkingValue)) {
        return REQUIRED_ERROR;
      } else if (!isBlank(checkingValue) && keepRef.current.validator) {
        return keepRef.current.validator(checkingValue);
      }
    },
    [value, required]
  );

  const validate = useCallback(
    (valueParam?: any) => {
      const newError = getError(valueParam ?? value);
      setError(newError);
      return newError;
    },
    [value, getError]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      keepRef.current.onBlur?.(event);

      setIsBlured(true);

      if (!['blur', 'blurChange'].includes(mode)) return;

      validate(event.target.value || '');
    },
    [mode, validate]
  );

  useEffect(() => {
    if (!isBlured || mode !== 'blurChange') {
      return;
    }

    const timeoutId = setTimeout(() => {
      validate(value);
    }, debounceMs);

    keepRef.current.timeoutId = timeoutId;

    return () => {
      clearTimeout(timeoutId);
    };
  }, [debounceMs, mode, value, isBlured, validate]);

  const result: TUseFieldValidationReturn = {
    mode,
    error,
    onBlur: handleBlur,
    getError,
    validate,
  };
  if (name) {
    fieldsRef.current[name] = result;
  }

  return result;
};

export default useFieldValidation;
